import { useEffect, useState } from "react"
import { ButtonDanger } from "components/ui/buttons/ButtonDanger/ButtonDanger"
import { ButtonOutline } from "components/ui/buttons/ButtonOutline/ButtonOutline"
import { TextArea } from "components/ui/inputs/TextArea"
import style from "./useReviewable.module.scss"

const { notePanel: notePanelCls, noteHeader, noteTitle, noteAction, noteTextareaWrap, noteTextarea } = style

type Args = {
	isReviewed?: boolean
	isPending?: boolean
	initialNote?: string
	onToggleReviewed?: (isReviewed: boolean) => void
	onSubmitNote?: (note: string) => void
	markReviewedLabel?: string
	markUnreviewedLabel?: string
}

export const useReviewable = ({
	isReviewed: controlledReviewed,
	isPending = false,
	initialNote = "",
	onToggleReviewed,
	onSubmitNote,
	markReviewedLabel = "Mark as reviewed",
	markUnreviewedLabel = "Mark as unreviewed",
}: Args = {}) => {
	const isControlled = controlledReviewed !== undefined
	const [internalReviewed, setInternalReviewed] = useState(false)
	const isReviewed = isControlled ? (controlledReviewed as boolean) : internalReviewed
	const [savedNote, setSavedNote] = useState(initialNote)
	const [isNoteOpen, setIsNoteOpen] = useState(initialNote.length > 0)
	const [note, setNote] = useState(initialNote)

	useEffect(() => {
		setSavedNote(initialNote)
		setNote(initialNote)
		setIsNoteOpen(initialNote.length > 0)
	}, [initialNote])

	const toggleReviewed = () => {
		const next = !isReviewed
		if (!isControlled) setInternalReviewed(next)
		if (!next) {
			setIsNoteOpen(false)
			setNote("")
			setSavedNote("")
		}
		onToggleReviewed?.(next)
	}

	const handleSubmit = () => {
		onSubmitNote?.(note)
		setSavedNote(note)
		if (note.length === 0) setIsNoteOpen(false)
	}

	const handleCancel = () => {
		setNote(savedNote)
		if (savedNote.length === 0) setIsNoteOpen(false)
	}

	const reviewButton = isReviewed ? (
		<ButtonDanger onClick={toggleReviewed} submittable={!isPending}>
			{markUnreviewedLabel}
		</ButtonDanger>
	) : (
		<ButtonOutline onClick={toggleReviewed} submittable={!isPending}>
			{markReviewedLabel}
		</ButtonOutline>
	)

	const isDirty = note !== savedNote

	const notePanel = isReviewed ? (
		<div className={notePanelCls}>
			<div className={noteHeader}>
				<span className={noteTitle}>Add a note for this?</span>
				{isNoteOpen ? (
					isDirty ? (
						<button type="button" className={noteAction} onClick={handleSubmit} disabled={isPending}>
							Submit
						</button>
					) : savedNote.length === 0 ? (
						<button type="button" className={noteAction} onClick={handleCancel}>
							Cancel
						</button>
					) : (
						<button type="button" className={noteAction} onClick={() => setIsNoteOpen(false)}>
							Hide
						</button>
					)
				) : (
					<button type="button" className={noteAction} onClick={() => setIsNoteOpen(true)}>
						Add
					</button>
				)}
			</div>
			{isNoteOpen ? (
				<div className={noteTextareaWrap}>
					<TextArea
						placeholder=""
						minRows={3}
						value={note}
						onChange={(e) => setNote(e.target.value)}
						className={noteTextarea}
					/>
				</div>
			) : null}
		</div>
	) : null

	return { isReviewed, notePanel, reviewButton }
}
